import { COLOR_PRESETS } from '@/hooks/useStoreSettings';
import { NAVBAR_STYLES, NAVBAR_HEIGHTS, FOOTER_STYLES, FOOTER_LAYOUTS } from './designConstants';
import { BrandSettings, SectionSaveState } from './designTypes';
import { SaveButton, ToggleRow } from './DesignShared';

interface Props {
  brand: BrandSettings;
  updateBrand: (patch: Partial<BrandSettings>) => void;
  navbarSaveState: SectionSaveState;
  footerSaveState: SectionSaveState;
  onSaveNavbar: () => void;
  onSaveFooter: () => void;
  selectedColor: typeof COLOR_PRESETS[0];
}

export default function BrandSection({
  brand, updateBrand,
  navbarSaveState, footerSaveState,
  onSaveNavbar, onSaveFooter,
  selectedColor,
}: Props) {
  const selectedFooter = FOOTER_STYLES.find(f => f.id === brand.footerStyle) ?? FOOTER_STYLES[0];

  return (
    <div className="flex flex-col gap-5">

      {/* ── Navbar Style ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
            <i className="ri-menu-line text-white text-[10px]"></i>
          </div>
          شريط التنقل (Navbar)
        </h3>
        <p className="text-xs text-gray-400 mb-4">اختر مظهر شريط التنقل العلوي للموقع</p>

        <div className="grid grid-cols-1 gap-2 mb-4">
          {NAVBAR_STYLES.map(style => {
            const previewBg = style.id === 'colored' ? selectedColor.primary
              : style.id === 'glass' ? 'rgba(10,10,10,0.6)'
              : style.bg;
            const previewText = style.id === 'light' ? '#1a1a1a' : '#ffffff';
            return (
              <button
                key={style.id}
                onClick={() => updateBrand({ navbarStyle: style.id })}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer text-right ${
                  brand.navbarStyle === style.id
                    ? 'border-[#1a1a1a] bg-white'
                    : 'border-gray-100 bg-white hover:border-gray-300'
                }`}
              >
                <div
                  className="w-20 h-8 rounded-lg flex items-center justify-between px-2 flex-shrink-0 overflow-hidden"
                  style={{
                    background: previewBg,
                    backdropFilter: style.id === 'glass' ? 'blur(8px)' : undefined,
                    border: style.id === 'light' ? '1px solid #e5e7eb' : undefined,
                  }}
                >
                  <div className="flex gap-1">
                    <div className="w-3 h-1.5 rounded-full" style={{ background: previewText, opacity: 0.6 }}></div>
                    <div className="w-3 h-1.5 rounded-full" style={{ background: previewText, opacity: 0.4 }}></div>
                  </div>
                  <div
                    className="text-[8px] font-black"
                    style={{
                      background: `linear-gradient(to left, ${selectedColor.primaryLight}, ${selectedColor.primary})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    إبداع
                  </div>
                </div>
                <div className="flex-1 text-right">
                  <p className="text-sm font-bold text-[#1a1a1a]">{style.label}</p>
                  <p className="text-xs text-gray-400">{style.desc}</p>
                </div>
                {brand.navbarStyle === style.id && (
                  <div className="w-5 h-5 flex items-center justify-center bg-[#1a1a1a] rounded-full flex-shrink-0">
                    <i className="ri-check-line text-white text-[10px]"></i>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="mb-4">
          <p className="text-xs font-bold text-gray-500 mb-2">ارتفاع الـ Navbar</p>
          <div className="grid grid-cols-3 gap-2">
            {NAVBAR_HEIGHTS.map(h => (
              <button
                key={h.id}
                onClick={() => updateBrand({ navbarHeight: h.id })}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                  brand.navbarHeight === h.id
                    ? 'border-[#1a1a1a] bg-white'
                    : 'border-gray-100 bg-white hover:border-gray-300'
                }`}
              >
                <div className="w-full flex items-center justify-center">
                  <div
                    className="w-12 rounded-lg bg-gray-200"
                    style={{ height: h.id === 'sm' ? '12px' : h.id === 'md' ? '18px' : '24px' }}
                  ></div>
                </div>
                <span className="text-xs font-bold text-[#1a1a1a]">{h.label}</span>
                <span className="text-[10px] text-gray-400">{h.value}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 mb-2">
          <p className="text-xs font-bold text-gray-500 mb-1">عناصر الـ Navbar</p>
          <ToggleRow
            icon="ri-pushpin-line" iconBg="bg-amber-100 text-amber-600"
            label="ثابت عند التمرير" desc="يبقى الـ navbar في الأعلى دائماً"
            value={brand.navbarSticky} onChange={v => updateBrand({ navbarSticky: v })}
          />
          <ToggleRow
            icon="ri-search-line" iconBg="bg-gray-100 text-gray-600"
            label="زر البحث" desc="إظهار أيقونة البحث في الـ navbar"
            value={brand.navbarShowSearch} onChange={v => updateBrand({ navbarShowSearch: v })}
          />
          <ToggleRow
            icon="ri-heart-line" iconBg="bg-red-100 text-red-500"
            label="زر المفضلة" desc="إظهار أيقونة المفضلة"
            value={brand.navbarShowWishlist} onChange={v => updateBrand({ navbarShowWishlist: v })}
          />
          <ToggleRow
            icon="ri-vip-crown-line" iconBg="bg-amber-100 text-amber-600"
            label="زر المكافآت" desc="إظهار أيقونة نقاط المكافآت"
            value={brand.navbarShowRewards} onChange={v => updateBrand({ navbarShowRewards: v })}
          />
        </div>

        <SaveButton state={navbarSaveState} onClick={onSaveNavbar} label="حفظ إعدادات الـ Navbar" />
      </div>

      {/* ── Footer Style ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
            <i className="ri-layout-bottom-line text-white text-[10px]"></i>
          </div>
          الفوتر (Footer)
        </h3>
        <p className="text-xs text-gray-400 mb-4">خصّص مظهر وألوان الجزء السفلي من الموقع</p>

        <div className="mb-4">
          <p className="text-xs font-bold text-gray-500 mb-2">لون خلفية الفوتر</p>
          <div className="grid grid-cols-3 gap-2">
            {FOOTER_STYLES.map(style => (
              <button
                key={style.id}
                onClick={() => updateBrand({ footerStyle: style.id })}
                className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                  brand.footerStyle === style.id ? 'border-[#1a1a1a]' : 'border-gray-100 hover:border-gray-300'
                }`}
                style={{ background: style.bg }}
              >
                <div className="flex gap-1 w-full justify-center">
                  <div className="w-4 h-1 rounded-full" style={{ background: style.text, opacity: 0.7 }}></div>
                  <div className="w-6 h-1 rounded-full" style={{ background: style.text, opacity: 0.4 }}></div>
                  <div className="w-3 h-1 rounded-full" style={{ background: style.text, opacity: 0.3 }}></div>
                </div>
                <span className="text-[10px] font-bold" style={{ color: style.text }}>{style.label}</span>
                {brand.footerStyle === style.id && (
                  <div className="w-4 h-4 flex items-center justify-center bg-white/20 rounded-full">
                    <i className="ri-check-line text-white text-[9px]"></i>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <p className="text-xs font-bold text-gray-500 mb-2">تخطيط الفوتر</p>
          <div className="grid grid-cols-2 gap-2">
            {FOOTER_LAYOUTS.map(layout => (
              <button
                key={layout.id}
                onClick={() => updateBrand({ footerLayout: layout.id })}
                className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer text-right ${
                  brand.footerLayout === layout.id
                    ? 'border-[#1a1a1a] bg-white'
                    : 'border-gray-100 bg-white hover:border-gray-300'
                }`}
              >
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${
                  brand.footerLayout === layout.id ? 'bg-[#1a1a1a]' : 'bg-gray-100'
                }`}>
                  <i className={`${layout.icon} text-sm ${brand.footerLayout === layout.id ? 'text-white' : 'text-gray-500'}`}></i>
                </div>
                <span className="text-xs font-bold text-[#1a1a1a]">{layout.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 mb-4">
          <p className="text-xs font-bold text-gray-500 mb-1">عناصر الفوتر</p>
          <ToggleRow
            icon="ri-instagram-line" iconBg="bg-pink-100 text-pink-500"
            label="أيقونات السوشيال ميديا" desc="إنستغرام، تويتر، سناب، تيك توك"
            value={brand.footerShowSocial} onChange={v => updateBrand({ footerShowSocial: v })}
          />
          <ToggleRow
            icon="ri-bank-card-line" iconBg="bg-green-100 text-green-600"
            label="طرق الدفع" desc="شعارات وسائل الدفع المقبولة"
            value={brand.footerShowPayments} onChange={v => updateBrand({ footerShowPayments: v })}
          />
          <ToggleRow
            icon="ri-time-line" iconBg="bg-amber-100 text-amber-600"
            label="ساعات العمل" desc="جدول أوقات الدوام"
            value={brand.footerShowHours} onChange={v => updateBrand({ footerShowHours: v })}
          />
        </div>

        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-gray-500">نص حقوق النشر</label>
            <input
              type="text"
              value={brand.footerCopyright}
              onChange={e => updateBrand({ footerCopyright: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-gray-500">وصف المتجر في الفوتر</label>
            <textarea
              value={brand.footerTagline}
              onChange={e => updateBrand({ footerTagline: e.target.value })}
              rows={2}
              maxLength={200}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white resize-none"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            />
            <p className="text-[10px] text-gray-400">{brand.footerTagline.length}/200 حرف</p>
          </div>
        </div>

        {/* Footer Live Preview */}
        <div className="mt-2 rounded-xl p-4 overflow-hidden" style={{ background: selectedFooter.bg }}>
          <p className="text-[10px] mb-3 flex items-center gap-1" style={{ color: selectedFooter.text, opacity: 0.6 }}>
            <i className="ri-eye-line"></i> معاينة الفوتر
          </p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            {['الروابط', 'الخدمات', 'التواصل'].map(col => (
              <div key={col}>
                <p className="text-[10px] font-bold mb-2" style={{ color: '#ffffff', opacity: 0.8 }}>{col}</p>
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-1.5 rounded-full mb-1.5" style={{ background: selectedFooter.text, opacity: 0.2, width: `${60 + i * 10}%` }}></div>
                ))}
              </div>
            ))}
          </div>
          <div className="border-t pt-3 flex items-center justify-between" style={{ borderColor: `${selectedFooter.text}20` }}>
            <p className="text-[9px] truncate" style={{ color: selectedFooter.text, opacity: 0.5 }}>
              {brand.footerCopyright.slice(0, 40)}...
            </p>
            {brand.footerShowSocial && (
              <div className="flex gap-1">
                {['ri-instagram-fill', 'ri-twitter-x-fill'].map(ic => (
                  <div key={ic} className="w-4 h-4 rounded flex items-center justify-center" style={{ background: `${selectedFooter.text}20` }}>
                    <i className={`${ic} text-[8px]`} style={{ color: selectedFooter.text, opacity: 0.6 }}></i>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <SaveButton state={footerSaveState} onClick={onSaveFooter} label="حفظ إعدادات الفوتر" />
      </div>
    </div>
  );
}
